import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SecurityService } from '@core/services';

import { SessionStorageService } from '@core/session-storage.service';
import { GridColumn, PrimeGridOption } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { DropdownItem } from '@shared/abstraction';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { ModalData } from '@shared/abstraction/modal/modal-data';
import { ModalOptions } from '@shared/abstraction/modal/modal-options';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { ImportFileModalComponent } from '@shared/components/import-file-modal/import-file-modal.component';
import { ImportResultModalComponent } from '@shared/components/import-result-modal/import-result-modal.component';
import { FileImportStatus } from '@shared/constants/file-import-status';
import { portfolioPaginationOptions } from '@shared/constants/pagination-settings';
import { NotificationStatus } from '@shared/enums/notification-status';
import { SecurityStatusName } from '@shared/enums/security-status-name';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { Client } from '@shared/models';
import { PrimeGridComponent } from '@shared/modules/grid/components/prime-grid/prime-grid.component';
import {
  AppNotificationService,
  HeaderService,
  ModalService,
} from '@shared/services';
import { UtilityService } from '@shared/services/utility.service';
import { QueryOptions } from 'odata-query';
import { Observable } from 'rxjs';
import { Collection } from 'src/app/modules/admin/models';
import { InvestmentJurisdictionService } from 'src/app/modules/admin/services';
import {
  CreateFileExportRequest,
  FileParameter,
  FileResponse,
  SecurityFileImportResponse,
  TransactionTypeEnum,
  UpdateSecurityTransactionTypeRequest,
} from 'src/app/web-api-client';
import { transactionTypeItems } from '../../../admin/constants/transaction-type-items';
import { SecurityGridData } from '../../models/security-grid-data';
import { ImportService } from '../../services/import.service';
import { PortfolioHeadersService } from '../../services/portfolio-headers.service';
import { PortfolioService } from '../../services/portfolio.service';
import { SecurityActionModalComponent } from '../security-action-date-modal/security-action-date-modal';

@Component({
  standalone: false,
  selector: 'gstar-portfolio',
  templateUrl: './portfolio.component.html',
  styleUrl: './portfolio.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PortfolioHeadersService],
})
export class PortfolioComponent implements OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  readonly portfolioService = inject(PortfolioService);
  private readonly modalService = inject(ModalService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly utilityService = inject(UtilityService);
  private readonly headerService = inject(HeaderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly importService = inject(ImportService);
  private readonly securityService = inject(SecurityService);
  private readonly columnsService = inject(PortfolioHeadersService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );

  @Input() transactionType!: TransactionType;
  public securities: WritableSignal<Collection<SecurityGridData>> = signal({
    total: 0,
    items: [],
  });

  grid: Signal<PrimeGridComponent<SecurityGridData> | undefined> =
    viewChild<PrimeGridComponent<SecurityGridData>>('grid');

  setting: PrimeGridOption<SecurityGridData> = {
    lazy: true,
    hasCheckbox: true,
    emptyMessage: '',
    collapsableRows: ['dividendsRate', 'capitalGainsRate'],
    isAccordionEnabled: true,
    actionMenu: (item: SecurityGridData): ActionButton<SecurityGridData>[] =>
      item.statusName !== SecurityStatusName.InformationRequired
        ? this.normalActionMenu
        : this.actionMenuForInfoRequired,
    isExpandedByDefault: false,
    paginationOptions: portfolioPaginationOptions,
    extendedPagination: true,
  };

  normalActionMenu: ActionButton<SecurityGridData>[] = [
    {
      label: this.translateService.instant('portfolio.actionMenu.view'),
      icon: '',
      command: (item: SecurityGridData): void => {
        this.navigateToViewSecurity(item);
      },
    },
    {
      label: this.translateService.instant('portfolio.actionMenu.edit'),
      icon: '',
      command: (item: SecurityGridData): void => {
        this.navigateToEditSecurity(item);
      },
    },
    {
      label: this.translateService.instant(
        'portfolio.actionMenu.dispose.buttonLabel'
      ),
      icon: '',
      command: (item: SecurityGridData): void => {
        this.modalService.open(
          SecurityActionModalComponent,
          {
            appModalId: 'dispose-confirm-modal',
          },
          {
            title: signal(
              this.translateService.instant(
                'portfolio.actionMenu.dispose.tittleModal'
              )
            ),
            content: signal(
              this.translateService.instant(
                'portfolio.actionMenu.dispose.contentModal'
              )
            ),
            placeHolderSecurityDateField: signal(
              this.translateService.instant(
                'portfolio.actionMenu.dispose.placeHolderSecurityDateField'
              )
            ),
            modalId: signal('dispose-confirm-modal'),
            creationDate: signal(item.created),
            acquisitionDate: signal(item.acquisitionDate),
            confirmationFn: (disposeDateField: Date) =>
              this.disposeSecurity(item, disposeDateField),
            isAcquire: signal(
              item.transactionType?.transactionTypeId ===
                TransactionType.SpecificAdvice
            ),
          }
        );
      },
    },
    {
      label: this.translateService.instant(
        'portfolio.actionMenu.delete.buttonLabel'
      ),
      icon: '',
      command: (item: SecurityGridData): void => {
        this.modalService.open(
          ConfirmationModalComponent,
          {
            appModalId: 'delete-confirm-modal',
          },
          {
            title: this.translateService.instant(
              'portfolio.actionMenu.delete.titleModal'
            ),
            content: this.translateService.instant(
              'portfolio.actionMenu.delete.contentModal'
            ),
            modalId: 'delete-confirm-modal',
            confirmationFn: () => this.deleteSecurity(item),
          }
        );
      },
    },
  ];

  actionMenuForInfoRequired: ActionButton<SecurityGridData>[] = [
    {
      label: this.translateService.instant(
        'portfolio.actionMenu.provideAddtionalInformation.buttonLabel'
      ),
      icon: '',
      command: (item: SecurityGridData): void => {
        this.navigateToEditSecurity(item);
      },
    },
    {
      label: this.translateService.instant(
        'portfolio.actionMenu.delete.buttonLabel'
      ),
      icon: '',
      command: (item: SecurityGridData): void => {
        this.modalService.open(
          ConfirmationModalComponent,
          {
            appModalId: 'delete-confirm-modal',
          },
          {
            title: this.translateService.instant(
              'portfolio.actionMenu.delete.titleModal'
            ),
            content: this.translateService.instant(
              'portfolio.actionMenu.delete.contentModal'
            ),
            modalId: 'delete-confirm-modal',
            confirmationFn: () => this.deleteSecurity(item),
          }
        );
      },
    },
  ];

  isLoading: WritableSignal<boolean> = signal(true);
  isTransactionTypeLoading: WritableSignal<boolean> = signal(false);
  enumTransactionTypeItems = TransactionType;
  securitiesSelected: WritableSignal<SecurityGridData[]> = signal<
    SecurityGridData[]
  >([]);
  importLoading: WritableSignal<boolean> = signal<boolean>(false);
  headers: WritableSignal<GridColumn[]> = signal([]);
  hasClientSelected: Signal<boolean> = computed(
    () => !!this.headerService.selectedClient()
  );

  gstarGrid = viewChild<PrimeGridComponent<SecurityGridData>>('grid');

  constructor() {
    const columnsService = this.columnsService;

    this.headers.set(columnsService.headers());
  }

  ngOnInit(): void {
    this.syncTransactionTypeFromUrl();

    this.headerService.selectedClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((client: Client | undefined) => {
        this.updateSelectedClient(client);
      });

    this.portfolioService.securitiesByClient
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securities: Collection<SecurityGridData>) => {
        this.loadSecurities(securities);
      });

    this.importService.fileImport
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fileImport: SecurityFileImportResponse) => {
        if (this.importLoading()) {
          this.processFileImportStatus(fileImport);
        }
      });

    this.setting.emptyMessage = this.translateService.instant(
      'portfolio.grid.noDataSecurities'
    );
  }

  ngOnDestroy(): void {
    this.modalService.closeAll();
    this.portfolioService.reset();
  }

  processFileImportStatus(fileImport: SecurityFileImportResponse): void {
    const fileImportStatusId = fileImport.fileImportStatus?.fileImportStatusId;
    if (fileImportStatusId === FileImportStatus.Completed) {
      this.openImportResultModal(fileImport.fileName, fileImport.history, true);
      this.refreshSecurities();
      this.importLoading.set(false);
    } else if (fileImportStatusId === FileImportStatus.Failed) {
      this.openImportResultModal(
        fileImport.fileName,
        fileImport.history,
        false
      );
      this.importLoading.set(false);
      this.importService.reloadFileImportStatus(0);
    } else {
      this.importService.reloadFileImportStatus(
        fileImport.securityFileImportId ?? 0
      );
    }
  }

  queryHandler(query: Partial<QueryOptions<SecurityGridData>>): void {
    this.startLoading();
    this.portfolioService.updateQuery(query);
  }

  private refreshSecurities(): void {
    this.startLoading();
    this.portfolioService.updateQuery(this.portfolioService.query());
  }

  // Rows must be emptied so the grid falls back to its skeleton template.
  private startLoading(): void {
    this.isLoading.set(true);
    this.securities.update(current => ({ ...current, items: [] }));
  }

  clearFilters(): void {
    this.columnsService.clearSecurityFilters();
  }

  selectTransactionType(item: DropdownItem): void {
    this.isLoading.set(true);
    this.isTransactionTypeLoading.set(true);
    this.securities.set({ total: 0, items: [] });
    this.securitiesSelected.set([]);
    this.portfolioService.updateTransactionType(item);
    this.grid()?.clearFiltersAndRun();
  }

  addSecurities(transactionType: TransactionType): void {
    if (transactionType === TransactionType.GeneralRates) {
      this.router.navigate(['/portfolio/add-general-rates']);
    } else {
      this.router.navigate(['/portfolio/add-specific-advice']);
    }
  }

  changeToSpecificAdvice(item: SecurityGridData, acquireDateField: Date): void {
    this.securityService
      .changeToSpecificAdvice(
        item.securityId as number,
        new UpdateSecurityTransactionTypeRequest({
          securityId: item.securityId as number,
          transactionTypeId: TransactionType.SpecificAdvice,
          acquisitionDate: acquireDateField,
        })
      )
      .subscribe(() => {
        this.refreshSecurities();

        this.notify(
          `notifications.changeToSpecificAdviceTitleSuccess`,
          `notifications.changeToSpecificAdviceDescriptionSuccess`,
          NotificationStatus.SUCCESS,
          {
            entityName: this.translateService
              .instant('notifications.changeToSpecificAdviceEntityName')
              .toLowerCase(),
          }
        );
      });
  }

  disposeSecurity(security: SecurityGridData, dispositionDate: Date): void {
    this.portfolioService
      .disposeAcquisition(security?.securityId ?? 0, dispositionDate)
      .subscribe(() => {
        this.refreshSecurities();

        this.notify(
          `notifications.disposeTitleSuccess`,
          `notifications.disposeDescriptionSuccess`,
          NotificationStatus.SUCCESS,
          {
            entityName: this.translateService
              .instant('notifications.disposeEntityName')
              .toLowerCase(),
          }
        );
      });
  }

  deleteSecurity(security: SecurityGridData): void {
    this.portfolioService
      .removeAcquisition(security?.securityId ?? 0)
      .subscribe(() => {
        this.refreshSecurities();

        this.notify(
          `notifications.deleteTitleSuccess`,
          `notifications.deleteDescriptionSuccess`,
          NotificationStatus.SUCCESS,
          {
            entityName: this.translateService
              .instant('notifications.deleteEntityName')
              .toLowerCase(),
          }
        );
      });
  }

  onSecuritySelected(selection: SecurityGridData[]): void {
    this.securitiesSelected.set(selection);
  }

  openImportResultModal(
    name?: string,
    content?: string,
    success?: boolean
  ): void {
    this.modalService.open(
      ImportResultModalComponent,
      {
        appModalId: 'import-result-modal',
      },
      {
        title: name,
        content,
        success,
        modalTitle: this.hasMessages(content)
          ? this.translateService.instant('notifications.importTitleSuccess', {
              fileName: name,
            })
          : this.translateService.instant(
              'notifications.importTitleSuccessNoEntities',
              { fileName: name }
            ),
        modalId: 'import-result-modal',
      }
    );
  }

  hasMessages(content?: string): boolean {
    if (
      content?.split('\n').filter((error: string) => error.length > 0).length
    ) {
      return true;
    }
    return false;
  }

  exportExcel(): void {
    this.modalService.open(
      ConfirmationModalComponent,
      {
        appModalId: 'export-confirm-modal',
      },
      {
        title: this.translateService.instant(
          'portfolio.exportToExcel.modalTittle'
        ),
        content: this.exportModalContent(),
        modalId: 'export-confirm-modal',
        confirmationFn: () => this.downloadExcel(),
      }
    );
  }

  importFile(): void {
    const modalId: string = 'import-file-modal';
    const modalOptions: ModalOptions = {
      appModalId: modalId,
    };
    const modalInput: ModalData = {
      title: this.translateService.instant('portfolio.import.title'),
      templateFileName: `GSTAR Portfolio sample file.xlsx`,
      description: this.translateService.instant('portfolio.import.message'),
      modalId,
      importFn: (file: FileParameter): void => {
        this.importService
          .importExcel(file)
          .subscribe((response: SecurityFileImportResponse) => {
            const fileImportId = response.securityFileImportId ?? 0;

            this.importLoading.set(true);
            this.importService.reloadFileImportStatus(fileImportId);
            this.notify(
              `notifications.fileProcessing`,
              `notifications.fileProcessingDescription`,
              NotificationStatus.SUCCESS
            );
          });
      },
      downloadTemplateFn: (): Observable<FileResponse> =>
        this.importService.downloadTemplate(),
    };
    this.modalService.open(ImportFileModalComponent, modalOptions, modalInput);
  }

  private notify(
    title: string,
    description: string,
    status: NotificationStatus,
    params?: JSONObject
  ): void {
    this.appNotificationService.notify(title, description, status, params);
  }

  private navigateToViewSecurity(item: SecurityGridData): void {
    this.router.navigate(['/portfolio', item.securityId, 'details']);
  }

  private navigateToEditSecurity(item: SecurityGridData): void {
    if (
      item.transactionType?.transactionTypeId === TransactionType.GeneralRates
    ) {
      this.router.navigate([
        '/portfolio',
        item.securityId,
        'edit-general-rates',
      ]);
    } else {
      this.router.navigate([
        '/portfolio',
        item.securityId,
        'edit-specific-advice',
      ]);
    }
  }

  exportModalContent(): string {
    const selectedClient: Client | undefined =
      this.headerService.selectedClient();
    let securitiesCount: number = this.securitiesSelected().length;
    if (this.grid()?.selectedAll()) {
      securitiesCount = this.grid()?.totalRows() ?? 0;
    }
    const securityNumber: string =
      securitiesCount === 1 ? '1 security' : `${securitiesCount} securities`;
    const client: string = selectedClient
      ? `client: '${selectedClient?.name}'`
      : 'all the clients';
    let filter: string = this.columnsService.buildFilterString(
      this.grid()?.filters()?.getActiveFilters()
    );
    if (filter) {
      filter = this.translateService.instant(
        'portfolio.exportToExcel.filterText',
        {
          filter,
        }
      );
    }
    return this.translateService.instant(
      'portfolio.exportToExcel.modalContent',
      {
        securityNumber,
        client,
        filter,
      }
    );
  }

  private downloadExcel(): void {
    const req = new CreateFileExportRequest({
      clientId: this.headerService.selectedClient()?.id,
      securityIds: this.securitiesSelected().map(
        (security: SecurityGridData) => security.securityId!
      ),
      transactionType: this.portfolioService.selectedTransactionType()
        .value as TransactionTypeEnum,
    });
    if (this.grid()?.selectedAll()) {
      this.portfolioService
        .exportAllToExcel(
          this.headerService.selectedClient()?.id,
          this.columnsService.securityFilters
        )
        .subscribe((template: FileResponse) => {
          this.downloadFile(template);
        });
    } else {
      this.portfolioService
        .exportToExcel(req)
        .subscribe((template: FileResponse) => {
          this.downloadFile(template);
        });
    }
  }

  private downloadFile(template: FileResponse): void {
    if (template.fileName === undefined) {
      template.fileName = `GSTAR Portfolio information for ${this.headerService.selectedClient()?.name} - ${this.utilityService.getCurrentFormattedDate()}.xlsx`;
    }
    this.utilityService.download(template.fileName!, template.data);
    this.notify(
      `notifications.exportTitleSuccess`,
      `notifications.exportDescriptionSuccess`,
      NotificationStatus.SUCCESS
    );
  }

  private updateSelectedClient(client: Client | undefined): void {
    this.portfolioService.updateClient(client);
    if (!this.portfolioService.isFirst()) {
      this.investmentJurisdictionService.reloadCountryList();
    }
    if (this.grid()) {
      this.grid()?.clearFiltersAndRun();
    }

    if (!client) {
      this.securitiesSelected.set([]);
    }
  }

  /**
   * Get the transaction type from the URL and update the portfolio service with it.
   * Works when we are redirected from Dashboard page
   * If the transaction type is not present in the URL, do nothing.
   */
  private syncTransactionTypeFromUrl(): void {
    if (this.transactionType === undefined || this.transactionType === null) {
      return;
    }

    const transactionTypeValue = Number(this.transactionType);

    if (Number.isNaN(transactionTypeValue)) {
      return;
    }

    const transactionTypeItem = transactionTypeItems.find(
      item => Number(item.value) === transactionTypeValue
    );

    if (!transactionTypeItem) {
      return;
    }

    this.portfolioService.updateTransactionType(transactionTypeItem);
  }

  private loadSecurities(securities: Collection<SecurityGridData>): void {
    const updatedSecurities = this.applyAccordionDisabledFlag(securities);
    this.securities.set(updatedSecurities);
    this.isLoading.set(false);
    this.isTransactionTypeLoading.set(false);
    this.securitiesSelected.set([]);

    if (this.grid()) {
      if (!this.grid()?.pageChangedManually()) {
        this.grid()?.toggleSelectAll(false);
      } else {
        this.grid()?.toggleSelectAll(true);
        this.grid()?.pageChangedManually.set(false);
      }
    }
  }

  private applyAccordionDisabledFlag(
    securities: Collection<SecurityGridData>
  ): Collection<SecurityGridData> {
    const maxAllowedShowRates = 2;

    securities.items = securities.items.map(security => ({
      ...security,
      isAccordionDisabled:
        security.transactionType?.transactionTypeId ===
          TransactionType.SpecificAdvice ||
        security.statusName !== SecurityStatusName.Approved ||
        (this.countRates(security.dividendsRate ?? '') <= maxAllowedShowRates &&
          this.countRates(security.capitalGainsRate ?? '') <=
            maxAllowedShowRates),
    }));
    return securities;
  }

  private countRates(rate: string): number {
    if (!rate) return 0;
    const items = rate.split(',').filter(item => item.trim() !== '');
    return items.length;
  }
}
